import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider, useSelector } from 'react-redux';
import { store } from './store';

// Hooks
import { useDeviceDetection } from './hooks/useDeviceDetection';
import { useAuthInit } from './hooks/useAuthInit';

// Components
import Layout from './components/common/Layout.jsx';
import MobileAppDownload from './components/mobile/MobileAppDownload.jsx';
import ErrorBoundary from './components/common/ErrorBoundary.jsx';
import { ToastProvider } from './components/common/Toast.jsx';

// Pages
import Feed from './pages/Feed.jsx';
import Profile from './pages/Profile.jsx';
import Analytics from './pages/Analytics.jsx';
import RunHistory from './pages/RunHistory.jsx';
import Explore from './pages/Explore.jsx';
import Login from './pages/Login.jsx';
import NotFound from './pages/NotFound.jsx';
import Admin from './pages/Admin.jsx';
import ResetPassword from './pages/ResetPassword.jsx';

// Loading component for auth initialization
const AuthLoading = () => (
  <div className="min-h-screen bg-gradient-vibe-subtle flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-pink border-t-transparent mx-auto mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

function AppContent() {
  const { isAuthenticated, isInitialized, isLoading } = useSelector((state) => state.auth);
  const isDesktop = useDeviceDetection();
  
  // Initialize Supabase auth listener
  useAuthInit();

  if (!isDesktop) {
    return <MobileAppDownload />;
  }

  // Show loading while checking for existing session
  if (!isInitialized && isLoading) {
    return <AuthLoading />;
  }

  return (
    <ErrorBoundary>
      <Router>
        <div className="App">
          {!isAuthenticated ? (
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/" element={<Navigate to="/login" replace />} />
              <Route path="/feed" element={<Navigate to="/login" replace />} />
              <Route path="/profile" element={<Navigate to="/login" replace />} />
              <Route path="/user/:userId" element={<Navigate to="/login" replace />} />
              <Route path="/analytics" element={<Navigate to="/login" replace />} />
              <Route path="/explore" element={<Navigate to="/login" replace />} />
              <Route path="/history" element={<Navigate to="/login" replace />} />
              <Route path="*" element={<NotFound isAuthenticated={false} />} />
            </Routes>
          ) : (
            <Routes>
              <Route path="/" element={<Layout />}>
                <Route index element={<Navigate to="/feed" replace />} />
                <Route path="feed" element={<ErrorBoundary><Feed /></ErrorBoundary>} />
                <Route path="profile" element={<ErrorBoundary><Profile /></ErrorBoundary>} />
                <Route path="user/:userId" element={<ErrorBoundary><Profile /></ErrorBoundary>} />
                <Route path="analytics" element={<ErrorBoundary><Analytics /></ErrorBoundary>} />
                <Route path="explore" element={<ErrorBoundary><Explore /></ErrorBoundary>} />
                <Route path="history" element={<ErrorBoundary><RunHistory /></ErrorBoundary>} />
                <Route path="admin" element={<ErrorBoundary><Admin /></ErrorBoundary>} />
              </Route>
              <Route path="/login" element={<Navigate to="/feed" replace />} />
              <Route path="*" element={<NotFound isAuthenticated={true} />} />
            </Routes>
          )}
        </div>
      </Router>
    </ErrorBoundary>
  );
}

function App() {
  return (
    <Provider store={store}>
      <ToastProvider>
        <AppContent />
      </ToastProvider>
    </Provider>
  );
}

export default App;
